import { useQuery } from "@tanstack/react-query";
import { type ChangeEvent, type FC, type KeyboardEvent, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import type {
	SeoCompetitor,
	SeoConfigDto,
	SeoLocationDto,
	SeoPromptCategory,
	SeoPromptEntry,
} from "shared";
import { useDebounceValue } from "usehooks-ts";
import { Api } from "../api.config";
import { Badge } from "../common/Badge";
import { Icon } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import { Card } from "../components/Card";
import { QueryKeys } from "../LinkGen";
import { ModalConfirmation } from "../settings/ModalConfirmation";
import { useSeoConfig, useSeoConfigActions } from "./useSeoConfig";
import "./SettingsTab.css";

const MIN_LOCATION_QUERY_LENGTH = 2;

const MAX_COMPETITORS = 4;
const MAX_KEYWORDS = 20;
const MAX_PROMPTS = 15;

const PROMPT_CATEGORIES: SeoPromptCategory[] = [
	"brand",
	"category",
	"local",
	"competitor",
];

const PROMPT_CATEGORY_LABELS: Record<SeoPromptCategory, string> = {
	brand: "Brand",
	category: "Category",
	local: "Local",
	competitor: "Competitor",
};

const SettingsTabEmpty: FC = () => {
	const { seed } = useSeoConfigActions();

	return (
		<Card className="seo-settings-empty">
			<Card.Header>Set up your SEO profile</Card.Header>
			<Card.Body>
				<p>
					Generate a starting SEO profile from your company information —
					market, competitors, tracked keywords, and AI prompts. You can review
					and adjust everything before saving.
				</p>
				<Button
					loading={seed.isPending}
					onClick={() => seed.mutate()}
					icon="add_circle"
				>
					Generate my SEO profile
				</Button>
			</Card.Body>
		</Card>
	);
};

type EditorProps = {
	config: SeoConfigDto;
};

const SettingsEditor: FC<Readonly<EditorProps>> = ({ config }) => {
	const { save, seed } = useSeoConfigActions();
	const { open, close } = useModal();
	const { id: companyId } = useCompany();

	const [market, setMarket] = useState(config.market);
	const [locationCode, setLocationCode] = useState<number | null>(
		config.locationCode,
	);
	const [locationName, setLocationName] = useState("");
	const [languageCode, setLanguageCode] = useState(config.languageCode);
	const [competitors, setCompetitors] = useState<SeoCompetitor[]>(
		config.competitors,
	);
	const [trackedKeywords, setTrackedKeywords] = useState<string[]>(
		config.trackedKeywords,
	);
	const [promptSet, setPromptSet] = useState<SeoPromptEntry[]>(
		config.promptSet,
	);
	const [keywordInput, setKeywordInput] = useState("");

	// `locationQuery` tracks the raw input for the controlled field and to
	// decide whether the results dropdown should render at all.
	// `debouncedQuery` only updates ~300ms after typing settles, and is what
	// actually drives the search query below — so fast typing coalesces into
	// a single request instead of one per keystroke.
	const [locationQuery, setLocationQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useDebounceValue("", 300);
	const trimmedQuery = locationQuery.trim();
	const trimmedDebouncedQuery = debouncedQuery.trim();
	const meetsMinLength = trimmedQuery.length >= MIN_LOCATION_QUERY_LENGTH;
	const debouncedMeetsMinLength =
		trimmedDebouncedQuery.length >= MIN_LOCATION_QUERY_LENGTH;

	const { data: locationResults = [], isFetching: isFetchingLocations } =
		useQuery({
			queryKey: QueryKeys.Seo.Locations(companyId, trimmedDebouncedQuery),
			queryFn: async () =>
				(await Api.SearchSeoLocations(trimmedDebouncedQuery)).data,
			enabled: debouncedMeetsMinLength,
		});

	// Query keys are scoped per debounced search string, so even if an
	// older, slower request resolves after a newer one, it only ever
	// populates the cache entry for its own (stale) key — it can never
	// clobber `locationResults` for the query string currently on screen.
	const isDebouncingLocation = trimmedQuery !== trimmedDebouncedQuery;
	const isSearchingLocations =
		meetsMinLength && (isDebouncingLocation || isFetchingLocations);

	const handleLocationQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocationQuery(value);
		setDebouncedQuery(value);
	};

	const handleSelectLocation = (location: SeoLocationDto) => {
		setLocationCode(location.locationCode);
		setLocationName(location.locationName);
		setLocationQuery("");
		setDebouncedQuery("");
	};

	const handleAddCompetitor = () => {
		if (competitors.length >= MAX_COMPETITORS) return;
		setCompetitors([...competitors, { name: "", domain: "" }]);
	};

	const handleCompetitorChange = (
		index: number,
		field: keyof SeoCompetitor,
		value: string,
	) => {
		setCompetitors(
			competitors.map((competitor, i) =>
				i === index ? { ...competitor, [field]: value } : competitor,
			),
		);
	};

	const handleRemoveCompetitor = (index: number) => {
		setCompetitors(competitors.filter((_, i) => i !== index));
	};

	const handleAddKeyword = () => {
		const value = keywordInput.trim();

		if (
			!value ||
			trackedKeywords.length >= MAX_KEYWORDS ||
			trackedKeywords.includes(value)
		) {
			setKeywordInput("");
			return;
		}

		setTrackedKeywords([...trackedKeywords, value]);
		setKeywordInput("");
	};

	const handleKeywordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAddKeyword();
		}
	};

	const handleRemoveKeyword = (keyword: string) => {
		setTrackedKeywords(trackedKeywords.filter((k) => k !== keyword));
	};

	const handleAddPrompt = () => {
		if (promptSet.length >= MAX_PROMPTS) return;
		setPromptSet([...promptSet, { prompt: "", category: "brand" }]);
	};

	const handlePromptTextChange = (index: number, value: string) => {
		setPromptSet(
			promptSet.map((prompt, i) =>
				i === index ? { ...prompt, prompt: value } : prompt,
			),
		);
	};

	const handlePromptCategoryChange = (
		index: number,
		value: SeoPromptCategory,
	) => {
		setPromptSet(
			promptSet.map((prompt, i) =>
				i === index ? { ...prompt, category: value } : prompt,
			),
		);
	};

	const handleRemovePrompt = (index: number) => {
		setPromptSet(promptSet.filter((_, i) => i !== index));
	};

	// The backend requires non-empty tracked-keyword and prompt lists on
	// save (an empty list would otherwise slip past the "setup required"
	// gates on Keyword Research / AI Visibility with nothing to run) — mirror
	// that here so the user gets a helpful inline hint instead of a raw 400.
	const filteredPromptCount = promptSet.filter((p) => p.prompt.trim()).length;
	const hasNoKeywords = trackedKeywords.length === 0;
	const hasNoPrompts = filteredPromptCount === 0;
	const saveDisabled = hasNoKeywords || hasNoPrompts;

	const handleSave = () => {
		if (saveDisabled) return;

		save.mutate({
			market,
			locationCode,
			languageCode,
			competitors: competitors.filter((c) => c.name.trim() && c.domain.trim()),
			trackedKeywords,
			promptSet: promptSet.filter((p) => p.prompt.trim()),
		});
	};

	const handleReseedClick = () => {
		open(ModalConfirmation, {
			icon: "refresh",
			variant: "primary",
			confirmText: "Re-seed with AI",
			prompt:
				"This will use AI to regenerate your SEO profile and overwrite any unsaved edits on this page. Continue?",
			onConfirm: async () => {
				await seed.mutateAsync();
				close();
			},
		});
	};

	const locationLabel = locationName
		? locationName
		: locationCode !== null
			? `Location code: ${locationCode}`
			: "No location selected";

	return (
		<div className="seo-settings-tab">
			<section className="seo-settings-section">
				<h5>Market</h5>
				<Row>
					<Col md={4}>
						<ExtraForm.Group label="Market" className="mb-3">
							<ExtraForm.Control
								type="text"
								placeholder="e.g. Austin, TX"
								value={market}
								onChange={(e) => setMarket(e.target.value)}
							/>
						</ExtraForm.Group>
					</Col>
					<Col md={3}>
						<ExtraForm.Group label="Language code" className="mb-3">
							<ExtraForm.Control
								type="text"
								placeholder="en"
								value={languageCode}
								onChange={(e) => setLanguageCode(e.target.value)}
							/>
						</ExtraForm.Group>
					</Col>
					<Col md={5}>
						<ExtraForm.Group label="Location" className="mb-3">
							<div className="seo-location-typeahead">
								<ExtraForm.Control
									type="text"
									placeholder="Search for a city or region"
									value={locationQuery}
									onChange={handleLocationQueryChange}
								/>
								{locationQuery && (
									<div className="seo-location-results">
										{!meetsMinLength && (
											<div className="seo-location-result-empty">
												Keep typing to search…
											</div>
										)}
										{meetsMinLength && isSearchingLocations && (
											<div className="seo-location-result-empty">
												Searching...
											</div>
										)}
										{meetsMinLength &&
											!isSearchingLocations &&
											locationResults.length === 0 && (
												<div className="seo-location-result-empty">
													No matches
												</div>
											)}
										{meetsMinLength &&
											!isSearchingLocations &&
											locationResults.map((location) => (
												<button
													type="button"
													key={location.locationCode}
													className="seo-location-result"
													onClick={() => handleSelectLocation(location)}
												>
													{location.locationName}
												</button>
											))}
									</div>
								)}
							</div>
							<Form.Text className="text-muted d-block mt-1">
								Selected: {locationLabel}
							</Form.Text>
						</ExtraForm.Group>
					</Col>
				</Row>
			</section>

			<section className="seo-settings-section">
				<h5>Competitors</h5>
				{competitors.map((competitor, index) => (
					<Row
						// biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id, order is user-controlled
						key={index}
						className="align-items-end mb-2"
					>
						<Col md={5}>
							<ExtraForm.Group label="Name" className="mb-0">
								<ExtraForm.Control
									type="text"
									value={competitor.name}
									onChange={(e) =>
										handleCompetitorChange(index, "name", e.target.value)
									}
									placeholder="Competitor name"
								/>
							</ExtraForm.Group>
						</Col>
						<Col md={5}>
							<ExtraForm.Group label="Domain" className="mb-0">
								<ExtraForm.Control
									type="text"
									value={competitor.domain}
									onChange={(e) =>
										handleCompetitorChange(index, "domain", e.target.value)
									}
									placeholder="example.com"
								/>
							</ExtraForm.Group>
						</Col>
						<Col md={2}>
							<ButtonIcon
								icon="delete"
								onClick={() => handleRemoveCompetitor(index)}
								aria-label="Remove competitor"
							/>
						</Col>
					</Row>
				))}
				<Button
					variant="ghost"
					size="sm"
					icon="add"
					disabled={competitors.length >= MAX_COMPETITORS}
					onClick={handleAddCompetitor}
				>
					Add competitor
				</Button>
				{competitors.length >= MAX_COMPETITORS && (
					<p className="text-muted seo-settings-limit-note">
						Maximum of {MAX_COMPETITORS} competitors.
					</p>
				)}
			</section>

			<section className="seo-settings-section">
				<h5>Tracked keywords</h5>
				<div className="seo-keyword-tags">
					{trackedKeywords.map((keyword) => (
						<Badge key={keyword}>
							{keyword}
							<Icon
								name="close"
								size={14}
								onClick={() => handleRemoveKeyword(keyword)}
								className="seo-keyword-tag-remove"
							/>
						</Badge>
					))}
				</div>
				<div className="seo-keyword-add">
					<ExtraForm.Control
						type="text"
						value={keywordInput}
						placeholder="Add a keyword"
						onChange={(e) => setKeywordInput(e.target.value)}
						onKeyDown={handleKeywordKeyDown}
						disabled={trackedKeywords.length >= MAX_KEYWORDS}
					/>
					<Button
						variant="ghost"
						size="sm"
						icon="add"
						disabled={trackedKeywords.length >= MAX_KEYWORDS}
						onClick={handleAddKeyword}
					>
						Add
					</Button>
				</div>
				{trackedKeywords.length >= MAX_KEYWORDS && (
					<p className="text-muted seo-settings-limit-note">
						Maximum of {MAX_KEYWORDS} tracked keywords.
					</p>
				)}
			</section>

			<section className="seo-settings-section">
				<h5>AI prompts</h5>
				{promptSet.map((prompt, index) => (
					<Row
						// biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id, order is user-controlled
						key={index}
						className="align-items-end mb-2"
					>
						<Col md={3}>
							<ExtraForm.Group label="Category" className="mb-0">
								<ExtraForm.Select
									value={prompt.category}
									onChange={(e) =>
										handlePromptCategoryChange(
											index,
											e.target.value as SeoPromptCategory,
										)
									}
								>
									{PROMPT_CATEGORIES.map((category) => (
										<option key={category} value={category}>
											{PROMPT_CATEGORY_LABELS[category]}
										</option>
									))}
								</ExtraForm.Select>
							</ExtraForm.Group>
						</Col>
						<Col md={7}>
							<ExtraForm.Group label="Prompt" className="mb-0">
								<ExtraForm.Control
									type="text"
									value={prompt.prompt}
									onChange={(e) =>
										handlePromptTextChange(index, e.target.value)
									}
									placeholder="e.g. Who are the best plumbers in Austin, TX?"
								/>
							</ExtraForm.Group>
						</Col>
						<Col md={2}>
							<ButtonIcon
								icon="delete"
								onClick={() => handleRemovePrompt(index)}
								aria-label="Remove prompt"
							/>
						</Col>
					</Row>
				))}
				<Button
					variant="ghost"
					size="sm"
					icon="add"
					disabled={promptSet.length >= MAX_PROMPTS}
					onClick={handleAddPrompt}
				>
					Add prompt
				</Button>
				{promptSet.length >= MAX_PROMPTS && (
					<p className="text-muted seo-settings-limit-note">
						Maximum of {MAX_PROMPTS} prompts.
					</p>
				)}
			</section>

			<div className="seo-settings-actions">
				<Button
					variant="ghost"
					icon="refresh"
					loading={seed.isPending}
					onClick={handleReseedClick}
				>
					Re-seed with AI
				</Button>
				<Button
					icon="save"
					loading={save.isPending}
					disabled={saveDisabled}
					onClick={handleSave}
				>
					Save
				</Button>
			</div>
			{saveDisabled && (
				<p className="text-muted seo-settings-limit-note">
					{hasNoKeywords && hasNoPrompts
						? "Add at least one tracked keyword and one AI prompt before saving."
						: hasNoKeywords
							? "Add at least one tracked keyword before saving."
							: "Add at least one AI prompt before saving."}
				</p>
			)}
		</div>
	);
};

export const SettingsTab: FC = () => {
	const config = useSeoConfig();

	if (!config) {
		return <SettingsTabEmpty />;
	}

	// Remount the editor whenever a new profile is seeded so local state
	// resets to the freshly generated values; saving does not change
	// seededAt, so in-progress edits are preserved across a save.
	return <SettingsEditor key={config.seededAt ?? "unseeded"} config={config} />;
};
